// Shared scaffold for the per-group admin "Members" section (circles,
// networks, spaces). The list/table shape is identical across all three —
// what differs is the action menu and what happens on each action. Each
// surface composes its own role/action cells and passes them to
// `buildMemberColumns` (basic roster) or `buildRosterColumns` (the
// leader-only roster, which additionally carries email, community standing,
// the member's circles in this network, and last-active). Roles are flat —
// every `role='leader'` row reads as "Leader"; there is no founder /
// primary / co-leader distinction.

import { type ReactNode, useState } from 'react';
import { useTranslations } from 'use-intl';
import {
  Avatar,
  Body,
  Button,
  Caption,
  Card,
  DataTable,
  I,
  Pill,
  Row,
  SearchInput,
  Skeleton,
  Stack,
  type ColumnDef,
} from '@leanin/ui';
import { Tooltip } from '@leanin/ui/client';
import { CONTINENTS, continentForCountry, type Continent } from '@leanin/shared';

import {
  AppliedFilters,
  FilterButton,
  FilterGroup,
  FilterSheet,
  PillFilter,
  type AppliedFilterChip,
} from '../../filters';
import type { GroupMemberView, GroupRosterMember } from '../../../lib/views/groups';
import type { DateFormatters } from '../../../lib/format-date';
import { GroupChips, type GroupChipItem } from '../../members';
import { MemberLink } from '../../MemberLink';
import { RouterLinkAs } from '../../../lib/router-link';
import { circlePath, networkPath } from '../../../lib/routes';
import styles from './members-table.module.css';

export function memberName(m: GroupMemberView, fallback = 'Member'): string {
  return `${m.firstName} ${m.lastName}`.trim() || fallback;
}

export function memberSublabel(m: GroupMemberView, fallback = 'Member'): string {
  return [m.jobTitle, m.company].filter(Boolean).join(' · ') || fallback;
}

// A member row as the filters see it: the basic roster has no email, circle
// list, or location; the leader roster has all three. Search covers email
// when present; the circle-leader filter needs the circles; the continent
// filter derives from `placeCountryCode` (present on the leader roster only,
// which is why the Location filter is data-gated at the call site).
type FilterableMember = GroupMemberView & {
  readonly email?: string;
  readonly placeCountryCode?: string | null;
  readonly circles?: ReadonlyArray<{
    readonly id: string;
    readonly role: 'leader' | 'member';
  }>;
};

// The sections all inject `useTranslations('Group')`. Naming that namespace
// (rather than the namespace-less `ReturnType<typeof useTranslations>`) keeps
// key lookups checked and stops tsc hitting its instantiation-depth limit on
// calls that pass ICU arguments.
type GroupTranslator = ReturnType<typeof useTranslations<'Group'>>;

// Join-recency windows offered on the members filter row. `''` is "any
// time" — an explicit, re-selectable option rather than a placeholder.
export const JOINED_WINDOWS = ['', '7d', '30d', '90d', '12m'] as const;
export type JoinedWindow = (typeof JOINED_WINDOWS)[number];

// `''` is "any role", same convention as the join window. `leader`/`member`
// are the member's role in the group being viewed — labelled by that group's
// kind ("Network leaders" on a network). The `circle_*` pair asks a different
// question: what she is in the Circles her row lists.
//
// Those two families collide on a circle's own page, where "Circle leaders"
// would mean both "leads this circle" and "leads some other circle", so the
// affiliation options are offered on networks only — the surface where
// "who runs Circles here" is the actual question.
export const ROLE_FILTERS = [
  '',
  'leader',
  'member',
  'circle_leader',
  'circle_member',
] as const;
export type RoleFilter = (typeof ROLE_FILTERS)[number];

export type MembersGroupKind = 'network' | 'circle' | 'space';

const ROLE_FILTERS_BY_KIND: Readonly<Record<MembersGroupKind, ReadonlyArray<RoleFilter>>> = {
  network: ROLE_FILTERS,
  circle: ['', 'leader', 'member'],
  space: ['', 'leader', 'member'],
};

// The committed filter selection the members sheet buffers and applies. Each
// field uses an empty sentinel for "any", matching `filterMembers`. Naming the
// shape pins the `FilterSheet` generic — an inline `emptyValues={{…, continents: []}}`
// would infer `continents: never[]` and widen `role`/`joinedWindow` to `string`.
type MembersFilterValues = {
  readonly role: RoleFilter;
  readonly joinedWindow: JoinedWindow;
  readonly continents: readonly Continent[];
};

const EMPTY_FILTER_VALUES: MembersFilterValues = {
  role: '',
  joinedWindow: '',
  continents: [],
};

// Cutoff for a window, or null for "any time". `now` is a parameter so the
// filter stays pure and testable against a frozen clock.
export function joinedWindowCutoff(window: JoinedWindow, now: Date): Date | null {
  const cutoff = new Date(now);
  switch (window) {
    case '':
      return null;
    case '7d':
      cutoff.setDate(cutoff.getDate() - 7);
      return cutoff;
    case '30d':
      cutoff.setDate(cutoff.getDate() - 30);
      return cutoff;
    case '90d':
      cutoff.setDate(cutoff.getDate() - 90);
      return cutoff;
    case '12m':
      cutoff.setMonth(cutoff.getMonth() - 12);
      return cutoff;
  }
}

function matchesRole(m: FilterableMember, role: RoleFilter): boolean {
  switch (role) {
    case '':
      return true;
    case 'leader':
    case 'member':
      return m.role === role;
    case 'circle_leader':
    case 'circle_member': {
      const want = role === 'circle_leader' ? 'leader' : 'member';
      // Mirrors the Circles column, which omits the group being viewed.
      return (m.circles ?? []).some((c) => c.role === want && c.id !== m.groupId);
    }
  }
}

// A member matches the continent filter when their country's continent is one
// of the selected set. A member with no country on file (or one outside the
// map) matches no continent, so any active continent filter excludes them.
function matchesContinent(
  m: FilterableMember,
  continents: readonly Continent[],
): boolean {
  if (continents.length === 0) return true;
  const continent = continentForCountry(m.placeCountryCode);
  return continent !== null && continents.includes(continent);
}

// Case-insensitive substring match on name / job title / company / email,
// intersected with the join-recency window, role, and continent. Filtering is
// client-side because the roster endpoints hand back the whole membership in
// one response — the same reason the CSV export can serialize exactly what the
// leader sees. Each dimension uses an empty sentinel for "any": `search === ''`,
// `role === ''`, `joinedWindow === ''`, `continents.length === 0`.
export function filterMembers<T extends FilterableMember>(args: {
  readonly members: ReadonlyArray<T>;
  readonly search: string;
  readonly joinedWindow: JoinedWindow;
  readonly role: RoleFilter;
  readonly continents: readonly Continent[];
  readonly now: Date;
}): ReadonlyArray<T> {
  const q = args.search.trim().toLowerCase();
  const cutoff = joinedWindowCutoff(args.joinedWindow, args.now);
  if (
    q.length === 0 &&
    cutoff === null &&
    args.role === '' &&
    args.continents.length === 0
  ) {
    return args.members;
  }
  const cutoffMs = cutoff?.getTime() ?? null;
  return args.members.filter((m) => {
    if (!matchesRole(m, args.role)) return false;
    if (!matchesContinent(m, args.continents)) return false;
    if (
      q.length > 0 &&
      !(
        memberName(m).toLowerCase().includes(q) ||
        (m.jobTitle ?? '').toLowerCase().includes(q) ||
        (m.company ?? '').toLowerCase().includes(q) ||
        (m.email ?? '').toLowerCase().includes(q)
      )
    ) {
      return false;
    }
    return cutoffMs === null || Date.parse(m.joinedAt) >= cutoffMs;
  });
}

export type MemberColumnsOptions<T extends GroupMemberView> = {
  readonly viewerUserId: string;
  /** Injected translator (`useTranslations('Group')`) from the calling
   * component — this builder is not a component and can't call the hook. */
  readonly t: GroupTranslator;
  /** Injected locale-aware date formatters (`useFormatDate()`), for the same
   * reason `t` is injected. */
  readonly fmt: DateFormatters;
  /** Role-cell renderer — every surface renders the same flat
   * "Leader"/"Member" labels; the prop hangs around so a per-kind variant
   * can swap colours if needed. */
  readonly renderRole: (m: T) => ReactNode;
  /** Action-cell renderer for non-viewer rows. Return `null` to omit the
   * actions column entirely (e.g. when a surface has no per-row actions). */
  readonly renderActions:
    | ((m: T, ctx: { readonly isYou: boolean; readonly name: string }) => ReactNode)
    | null;
};

function primaryColumn<T extends GroupMemberView>(
  opts: MemberColumnsOptions<T>,
  emailOf: ((m: T) => string) | null,
  inlineBadges: ((m: T) => ReactNode) | null,
): ColumnDef<T> {
  const memberFallback = opts.t('members.memberFallback');
  return {
    id: 'member',
    header: opts.t('members.table.member'),
    meta: { mobilePrimary: true },
    cell: ({ row }) => {
      const m = row.original;
      const name = memberName(m, memberFallback);
      const sublabel = memberSublabel(m, '');
      const isYou = m.userId === opts.viewerUserId;
      return (
        <Row gap="md" align="center" style={{ minWidth: 0 }}>
          {/* Avatar and name both link to the profile — the avatar is the
              bigger target and the one people reach for. It is marked
              decorative so the row exposes a single link to assistive tech. */}
          <MemberLink userId={m.userId} linkAs={RouterLinkAs} decorative>
            <Avatar src={m.photoUrl ?? undefined} name={name} size={36} />
          </MemberLink>
          <Stack gap="xs" style={{ minWidth: 0, flex: 1 }}>
            <Row gap="xs" align="center" wrap style={{ minWidth: 0 }}>
              <MemberLink
                userId={m.userId}
                linkAs={RouterLinkAs}
                className={styles.memberLink}
              >
                <Body as="span" size="md">
                  {name}
                </Body>
              </MemberLink>
              {isYou ? <Pill tone="burgundy">{opts.t('members.you')}</Pill> : null}
              {/* The roster carries the group-role pill here rather than in
                  its own column: seven columns overflowed the viewport, and
                  the role belongs with the identity it qualifies. */}
              {inlineBadges !== null ? inlineBadges(m) : null}
            </Row>
            {/* No job title and no company means there is nothing to say —
                printing the generic "Member" fallback here reads as a
                contradiction directly under a "Leader" pill. */}
            {sublabel === '' ? null : <Caption>{sublabel}</Caption>}
            {/* Email sits in the identity cell rather than its own column:
                the leader roster is already 7 columns wide, and an address
                belongs with the name it identifies. */}
            {emailOf !== null ? <Caption>{emailOf(m)}</Caption> : null}
            {/* On mobile the role/joined/actions columns are hidden; show them
                inline here so the card row stays compact. Plain div so the
                CSS display:none isn't overridden by Row's inline flex style. */}
            <div className={styles.mobileMetaRow}>
              {/* The roster already shows the role inline above, so repeating
                  it here would double the pill on mobile. */}
              {inlineBadges === null ? opts.renderRole(m) : null}
              <Caption>{opts.fmt.medium(m.joinedAt)}</Caption>
              {opts.renderActions !== null && !isYou ? (
                <div style={{ marginInlineStart: 'auto' }}>
                  {opts.renderActions(m, { isYou, name })}
                </div>
              ) : null}
            </div>
          </Stack>
        </Row>
      );
    },
  };
}

function roleColumn<T extends GroupMemberView>(
  opts: MemberColumnsOptions<T>,
): ColumnDef<T> {
  return {
    id: 'role',
    header: opts.t('members.table.role'),
    meta: { width: 160, nowrap: true, mobileHide: true },
    cell: ({ row }) => opts.renderRole(row.original),
  };
}

function joinedColumn<T extends GroupMemberView>(
  opts: MemberColumnsOptions<T>,
): ColumnDef<T> {
  return {
    id: 'joined',
    header: opts.t('members.table.joined'),
    // Sorted on the parsed timestamp, not the rendered label — "Mar 2, 2025"
    // sorts alphabetically into nonsense. Newest-first on first click, which
    // is what a leader asking "who just joined?" wants.
    accessorFn: (m) => Date.parse(m.joinedAt),
    sortingFn: 'basic',
    sortDescFirst: true,
    meta: { width: 120, nowrap: true, mobileHide: true },
    cell: ({ row }) => (
      <Body size="sm" color="mute">
        {opts.fmt.medium(row.original.joinedAt)}
      </Body>
    ),
  };
}

function actionsColumn<T extends GroupMemberView>(
  opts: MemberColumnsOptions<T>,
  renderActions: NonNullable<MemberColumnsOptions<T>['renderActions']>,
): ColumnDef<T> {
  const memberFallback = opts.t('members.memberFallback');
  return {
    id: 'actions',
    header: '',
    meta: { width: 90, align: 'end', nowrap: true, mobileHide: true },
    cell: ({ row }) => {
      const m = row.original;
      const name = memberName(m, memberFallback);
      const isYou = m.userId === opts.viewerUserId;
      if (isYou) {
        return (
          <Body size="sm" color="mute">
            —
          </Body>
        );
      }
      return renderActions(m, { isYou, name });
    },
  };
}

export function buildMemberColumns<T extends GroupMemberView>(
  opts: MemberColumnsOptions<T>,
): ColumnDef<T>[] {
  const cols: ColumnDef<T>[] = [
    primaryColumn(opts, null, null),
    roleColumn(opts),
    joinedColumn(opts),
  ];
  if (opts.renderActions !== null) {
    cols.push(actionsColumn(opts, opts.renderActions));
  }
  return cols;
}

// Affiliations other than the group being viewed — plain name pills, no
// encoding to decode. The group you are administering is filtered out: it is
// true of every row, so repeating it says nothing. Each pill links to that
// circle or network so a leader can open it from the row.
function affiliationChips(
  m: GroupRosterMember,
  kind: 'circles' | 'networks',
): GroupChipItem[] {
  if (kind === 'circles') {
    return m.circles
      .filter((c) => c.id !== m.groupId)
      .map((c) => ({
        key: c.id,
        label: c.name,
        tone: 'neutral' as const,
        href: circlePath(c.id),
      }));
  }
  return m.networks
    .filter((n) => !n.isThisGroup)
    .map((n) => ({
      key: n.id,
      label: n.name,
      tone: 'navy' as const,
      href: networkPath(n.id),
    }));
}

// The leader-only roster: everything `buildMemberColumns` renders, plus the
// columns that only a leader of this group may see. `groupKind` names the
// affiliation headers after what each column actually excludes: a circle page
// omits the circle being viewed ("Other Circles" / "Networks"), a network
// page omits the network ("Circles" / "Other networks").
export function buildRosterColumns<T extends GroupRosterMember>(
  opts: MemberColumnsOptions<T> & { readonly groupKind: 'network' | 'circle' },
): ColumnDef<T>[] {
  const cols: ColumnDef<T>[] = [
    primaryColumn(
      opts,
      (m) => m.email,
      (m) => (
        <>
          {opts.renderRole(m)}
          {m.membershipSource === 'via_circle' ? (
            <Tooltip content={opts.t('members.viaCircleHint')}>
              <Pill tone="neutral" size="sm">
                {opts.t('members.viaCircle')}
              </Pill>
            </Tooltip>
          ) : null}
        </>
      ),
    ),
    {
      id: 'circles',
      header: opts.t(`members.table.circles.${opts.groupKind}`),
      meta: { width: 170, mobileHide: true },
      cell: ({ row }) => (
        <GroupChips
          items={affiliationChips(row.original, 'circles')}
          show={2}
          maxWidth={170}
          moreLabel={(count) => opts.t('members.affiliations.showMore', { count })}
        />
      ),
    },
    {
      id: 'otherNetworks',
      header: opts.t(`members.table.otherNetworks.${opts.groupKind}`),
      meta: { width: 170, mobileHide: true },
      cell: ({ row }) => (
        <GroupChips
          items={affiliationChips(row.original, 'networks')}
          maxWidth={170}
          moreLabel={(count) => opts.t('members.affiliations.showMore', { count })}
        />
      ),
    },
    joinedColumn(opts),
  ];
  if (opts.renderActions !== null) {
    cols.push(actionsColumn(opts, opts.renderActions));
  }
  return cols;
}

// Search + join-recency filters, the table, an optional CSV export, and the
// count line. Owns the filter state so all three member sections (and the
// export, which serializes exactly the filtered rows) stay in step.
export function MembersTablePanel<T extends FilterableMember>({
  members,
  columns,
  t,
  searchPlaceholder,
  emptyMessage,
  totalCount,
  countSummary,
  onExport,
  exportLabel,
  groupKind,
  now = new Date(),
}: {
  readonly members: ReadonlyArray<T>;
  readonly columns: ColumnDef<T>[];
  readonly t: GroupTranslator;
  readonly searchPlaceholder: string;
  readonly emptyMessage: string;
  readonly totalCount: number;
  /** Rendered as-is below the table — a surface that wants extra copy beside
   *  the count returns its own layout rather than nesting a block element
   *  inside a `<Body>` paragraph. */
  readonly countSummary: (shown: number, total: number) => ReactNode;
  /** Receives the rows the filters currently show, in display order. */
  readonly onExport?: (rows: ReadonlyArray<T>) => void;
  readonly exportLabel?: string;
  /** Names the role options ("Network leaders" vs "Circle leaders") and
   *  decides whether the circle-affiliation options are offered. */
  readonly groupKind: MembersGroupKind;
  readonly now?: Date;
}) {
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<RoleFilter>('');
  const [joinedWindow, setJoinedWindow] = useState<JoinedWindow>('');
  const [continents, setContinents] = useState<readonly Continent[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const visible = filterMembers({ members, search, joinedWindow, role, continents, now });

  // The Location filter only makes sense where members carry a country. The
  // leader roster (circle / network) sets `placeCountryCode`; the basic member
  // list (spaces) does not. Data-gating keeps location off the member-visible
  // list entirely and hides an empty filter on a roster where no one has a
  // place set.
  const hasLocationData = members.some((m) => m.placeCountryCode != null);

  const roleLabelOf = (value: Exclude<RoleFilter, ''>): string =>
    value === 'leader' || value === 'member'
      ? t(`members.roleFilter.${groupKind}.${value}`)
      : t(`members.roleFilter.${value}`);

  const roleOptions = ROLE_FILTERS_BY_KIND[groupKind]
    .filter((value): value is Exclude<RoleFilter, ''> => value !== '')
    .map((value) => ({ value, label: roleLabelOf(value) }));
  const windowOptions = JOINED_WINDOWS.filter(
    (value): value is Exclude<JoinedWindow, ''> => value !== '',
  ).map((value) => ({ value, label: t(`members.joinedWindow.${value}`) }));
  const continentOptions = CONTINENTS.map((value) => ({
    value,
    label: t(`members.continent.${value}`),
  }));

  const activeFilterCount =
    (role !== '' ? 1 : 0) + (joinedWindow !== '' ? 1 : 0) + continents.length;

  const chips: AppliedFilterChip[] = [];
  if (role !== '') {
    chips.push({
      id: `role:${role}`,
      label: roleLabelOf(role),
      tone: 'burgundy',
      onRemove: () => setRole(''),
    });
  }
  if (joinedWindow !== '') {
    chips.push({
      id: `joined:${joinedWindow}`,
      label: t(`members.joinedWindow.${joinedWindow}`),
      onRemove: () => setJoinedWindow(''),
    });
  }
  for (const value of continents) {
    chips.push({
      id: `continent:${value}`,
      label: t(`members.continent.${value}`),
      onRemove: () => setContinents((prev) => prev.filter((c) => c !== value)),
    });
  }

  const clearAll = () => {
    setRole('');
    setJoinedWindow('');
    setContinents([]);
  };

  return (
    <Stack gap="md">
      <Row gap="sm" align="center" justify="space-between" wrap>
        <Row gap="sm" align="center" wrap>
          <SearchInput
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <FilterButton
            count={activeFilterCount}
            onClick={() => setFiltersOpen(true)}
            aria-expanded={filtersOpen}
          />
        </Row>
        {onExport !== undefined ? (
          <Button
            variant="secondary"
            size="md"
            icon={I.download}
            onClick={() => onExport(visible)}
            disabled={visible.length === 0}
          >
            {exportLabel}
          </Button>
        ) : null}
      </Row>
      <AppliedFilters chips={chips} onClearAll={clearAll} />
      <DataTable
        // Remount on a filter change so client-side pagination returns to
        // page 1 instead of stranding the leader on a now-empty page. Every
        // filter dimension is in the key — omitting one strands pagination
        // when only that dimension changes.
        key={`${search}|${role}|${joinedWindow}|${[...continents].sort().join(',')}`}
        data={[...visible]}
        columns={columns}
        pagination={{ pageSize: 10 }}
        sortable
      >
        <DataTable.Header />
        <DataTable.Body emptyMessage={emptyMessage} />
        <DataTable.Pagination />
      </DataTable>
      {countSummary(visible.length, totalCount)}
      <FilterSheet<MembersFilterValues>
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        title={t('members.filters.sheetTitle')}
        description={t('members.filters.sheetDescription')}
        values={{ role, joinedWindow, continents }}
        emptyValues={EMPTY_FILTER_VALUES}
        onApply={(next) => {
          setRole(next.role);
          setJoinedWindow(next.joinedWindow);
          setContinents(next.continents);
        }}
      >
        {(draft, setDraft) => (
          <>
            <FilterGroup label={t('members.filters.role')}>
              <PillFilter
                value={draft.role}
                onChange={(next) => setDraft({ ...draft, role: next as RoleFilter })}
                allowDeselect
                options={roleOptions}
              />
            </FilterGroup>
            <FilterGroup label={t('members.filters.joinDate')}>
              <PillFilter
                value={draft.joinedWindow}
                onChange={(next) =>
                  setDraft({ ...draft, joinedWindow: next as JoinedWindow })
                }
                allowDeselect
                options={windowOptions}
              />
            </FilterGroup>
            {hasLocationData ? (
              <FilterGroup label={t('members.filters.location')}>
                <PillFilter
                  multi
                  value={draft.continents}
                  onChange={(next) =>
                    setDraft({ ...draft, continents: next as Continent[] })
                  }
                  options={continentOptions}
                />
              </FilterGroup>
            ) : null}
          </>
        )}
      </FilterSheet>
    </Stack>
  );
}

// Loading-state surrogate while the membership query is in flight.
// Matches the migrated DataTable layout: skeleton header → skeleton rows
// inside a Card+border. Replaces the old AdminMembersListSkeleton.
export function MembersTableSkeleton({
  rows = 6,
  withActions = true,
}: {
  rows?: number;
  withActions?: boolean;
}) {
  return (
    <Stack gap="md">
      <Skeleton width={320} height={36} rounded="md" />
      <Card pad="flush" style={{ overflow: 'hidden' }}>
        <Stack gap="none">
          {Array.from({ length: rows }, (_, i) => (
            <Row
              key={i}
              gap="md"
              align="center"
              py="md"
              px="lg"
              style={{ borderBottom: '1px solid var(--border)' }}
            >
              <Skeleton width={36} height={36} rounded="full" />
              <Stack gap="xs" style={{ flex: 1 }}>
                <Skeleton width={140} height={14} rounded="sm" />
                <Skeleton width={200} height={12} rounded="sm" />
              </Stack>
              <Skeleton width={88} height={20} rounded="full" />
              <Skeleton width={80} height={14} rounded="sm" />
              {withActions ? <Skeleton width={72} height={28} rounded="md" /> : null}
            </Row>
          ))}
        </Stack>
      </Card>
    </Stack>
  );
}
